import type { JSX } from "react";

import type { Dayjs } from "dayjs";

import type { IRecentObject } from "app/@types/userdata";
import type { ICampaignsScrapingConfiguration } from "app/components/campaigns/shared/CampaignsConst";
import type { ITableWidget } from "app/components/widget/dashboard/TableTemplate-v2";
import type { EGranularities } from "app/pages/app-intelligence/constants";
import type {
    IKeywordsCampaignData,
    IKeywordsCampaignsScrapingKeywords,
    IRTCurrentCampaignData,
    RtCampaignScrapeContext,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { IRankTrackerChangeProps } from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/expandedTable/RankTrackerExpandedTableTypes";
import type {
    IGeoLocation,
    IReportTableMetrics,
} from "app/pages/digital-marketing/keywords-campaigns/reports/shared/Table/TableUtils/RankTrackerTablesTypes";
import type { RTRawParams } from "app/pages/digital-marketing/keywords-campaigns/reports/types";
import type { ISupportedLanguagesAndLocale } from "app/pages/digital-marketing/keywords-campaigns/wizard/campaigns-wizard/types";

export interface IDataPoint {
    key: string;
    value: number;
}

export interface ISelectedSites extends IRecentObject {
    isPrimary: boolean;
    color: string;
}

export interface IKeywordsReportSitesFiltersProps {
    availableSites: ISelectedSites[];
    onNewCompetitorSelected?: (newSelectedSite: string) => void;
    onCompareClickCallback?: VoidFunction;
}
export interface IKeywordsReportTableCompetitorsProps {
    availableSites: ISelectedSites[];
    onCompetitorRemoved: (newSelectedSite: string) => void;
    onNewCompetitorSelected: (newSelectedSite: string) => void;
    onClearAllCompetitors: VoidFunction;
    currentCampaignConfiguration: IRTCurrentCampaignData;
    setIsCompetitorsEditModalOpen: (isOpen: boolean) => void;
}
export interface IKeywordsReportTableCompetitorItemProps {
    siteId: string;
    isMainSite: boolean;
    allCompetitorsSelected?: boolean;
    availableSites: ISelectedSites[];
    onCompetitorRemoved?: (newSelectedSite: string) => void;
    onCompetitorReplaced: (removedSelectedSite: string, newSelectedSite: string) => void;
    siteAsset: string;
    siteColor: string;
    editItemAllowed: boolean;
    isRemoveCompetitorAllowed: boolean;
    isCompareMode?: boolean;
    isOverlapping?: boolean;
    autocompletePlaceholder?: string;
    iconsArray?: string[];
}

export interface IAutocompleteStyledKeywordsCampaignsProps {
    getListItems?(query: string): JSX.Element;
    isFocused: boolean;
    onBlur?: VoidFunction;
    isOverlapping?: boolean;
}

export enum EKeywordsReportKeywordsTableMetrics {
    POSITION = "position",
    UNIVERSAL_POSITION = "overallPosition",
    ALL = "all",
    CLICKS = "clicks",
}

export interface IKeywordsReportRecordsRawData {
    keyword: string;
    tags: string[];
    topUrl: string;
    geoLocation: string;
    scrapingConfigurationId: string;
    position: { [site: string]: IRankTrackerChangeProps };
    trend: IDataPoint[];
    volume: number;
    cpc: number;
    difficulty: number;
    lastUpdated?: string | null;
    intent: string;
    urlCount: number;
    serpFeatures: {
        keywordSerpFeatures: string[];
        siteSerpFeatures: {
            [key: string]: string[];
        };
    };
    targetUrls: string[];
}

type IKeywordsReportsTableTopTableData = Partial<IKeywordsReportRecordsRawData>;

interface IKeywordsReportSERPSnapshotRecordsData {
    geoLocation: string;
    locale: string;
    searchEngine: string;
    campaignId: string;
    keyword: string;
    device: string;
    date: string;
    link: string;
}

interface IAiTaggingMixin {
    aiTagging?: {
        isLoading: boolean;
    };
}

export interface IKeywordsReportSERPSnapshotData {
    records: IKeywordsReportSERPSnapshotRecordsData[];
    totalCount: number;
}

export interface IKeywordsReportTableData extends IAiTaggingMixin {
    records: IKeywordsReportRecordsRawData[];
    totalCount: number;
}

export interface IKeywordsReportTableDataWithKeys extends IKeywordsReportTableData {
    records: Array<IKeywordsReportRecordsRawData & { key: string }>;
}

export interface IKeywordsReportTableMetrics extends IReportTableMetrics {
    metricID: EKeywordsReportKeywordsTableMetrics;
    switcherTranslationKey: string;
    tooltipKey: string;
    columnPickerKey: string;
    maxChangeValidValue?: number | null;
}
export interface ISitesForSerpColumn {
    name: string;
    color: string;
}

export interface IKeywordsReportSitesSelectorProps {
    onCompetitorRemoved: (removedSelectedSite: string) => void;
    onCompetitorReplaced: (removedSelectedSite: string, newSelectedSite: string) => void;
}

export interface IKeywordsReportRecordManagedData extends IAiTaggingMixin {
    key: string;
    keyword: string;
    urlCount: number;
    targetUrl: string;
    volume: number;
    cpc: string;
    intent: {
        PrimaryIntent: string;
    };
    tags: {
        text: string;
    }[];
    duration: string;
    campaignGuid: string;
    SerpFeatures: string[];
    SiteSerpFeatures: Record<string, string[]>;
    geoLocation: IGeoLocation;
    searchEngine: string;
    lastUpdated?: string | null;
    lastUpdatedFormatted: string;
    keywordToTargetUrls: string[];
    scrapingConfigurationId: string;
    numberOfUrls: {
        amountOfAdditionalSites: number;
        site: string;
        displayedSite?: string;
        tooltipAdditionalSites: string;
    };
    [key: string]: unknown;
}

export interface IKeywordsTableTableTopProps {
    onRemoveKeywordsClicked: VoidFunction;
    tagsFromTable: Set<string>;
    onUpdateFromTableCellSucceededCallback: VoidFunction;
    tableStateKey: string;
    tableDataRecords: IKeywordsReportsTableTopTableData[];
    setIsTargetUrlModalOpen?: (isOpen: boolean) => void;
    onUpdateTableError?: (error: Error, toastText: string) => void;
    onUpdateTableSucceeded?: (toastText: string) => void;
    showEditTargetUrl: boolean;
    updateTargetUrlOfKeywords?: (targetUrl: string[], targetUrlRow: IKeywordsReportRecordManagedData) => void;
}

export type ScrapingConfigurationWithoutKeywords = Omit<ICampaignsScrapingConfiguration, "keywords">;

export type CampaignConfigForDashboard = Omit<IRTCurrentCampaignData, "tags"> & {
    scrapingConfigurations: ScrapingConfigurationWithoutKeywords[];
};

export interface IRankTrackerKeywordsReportTableWidget extends ITableWidget {
    parsedCampaigns: IKeywordsCampaignData[];
    parsedLanguages: ISupportedLanguagesAndLocale[];
}

export interface ITransferDataToRowProps {
    data: IKeywordsReportTableData;
    tableTab: string;
    tableGranularity: EGranularities;
    selectedSites: ISelectedSites[];
    scrapingConfigurations: ICampaignsScrapingConfiguration[];
    languagesList: ISupportedLanguagesAndLocale[];
    duration: string;
    campaign: RtCampaignScrapeContext;
    device: string;
    pageNumber?: number;
    isDemoCampaign: boolean;
    keywordToTargetUrls: IKeywordsCampaignsScrapingKeywords;
}

export type RTKeywordsRawParams = RTRawParams & {
    tableTab?: EKeywordsReportKeywordsTableMetrics;
    tableView?: string;
};
